import { Button, Group, Text } from '@mantine/core';
import { normalizeOperationOutcome } from '@medplum/core';
import { Attachment, OperationOutcome, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useDebouncedCallback } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
export interface SignaturePadProps {
  readonly securityContext?: Reference;
  readonly onUpload: (attachment: Attachment) => void;
  readonly onUploadError?: (outcome: OperationOutcome) => void;
  readonly disabled?: boolean;
}

export const SignaturePad = (props: SignaturePadProps): JSX.Element => {
  const medplum = useMedplum();
  const sigpadRef = useRef<SignatureCanvas | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writing, setWriting] = useState(true);
  const { t } = useTranslation(['medplum-react']);

  const saveSignature = useCallback(async () => {
    if (!sigpadRef.current) {
      return;
    }

    const base64Image = sigpadRef.current
      .getTrimmedCanvas()
      .toDataURL('image/png');
    if (!base64Image) {
      return;
    }

    setIsSaving(true);

    const base64WithoutPrefix = base64Image.replace(
      /^data:image\/png;base64,/,
      ''
    );
    const binaryData = atob(base64WithoutPrefix);
    const arrayBuffer = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      arrayBuffer[i] = binaryData.charCodeAt(i);
    }

    const blob = new Blob([arrayBuffer], { type: 'image/png' });
    const file = new File([blob], 'signature.png', { type: 'image/png' });

    try {
      const attachment = await medplum.createAttachment({
        data: file,
        contentType: 'image/png',
        filename: 'signature.png',
        securityContext: props.securityContext,
      });

      props.onUpload(attachment);
      setIsSaving(false);
    } catch (err) {
      setIsSaving(false);
      if (props.onUploadError) {
        props.onUploadError(normalizeOperationOutcome(err));
      }
    }
  }, [medplum, props]);

  const debouncedSaveSignature = useDebouncedCallback(
    () => {
      if (writing) {
        return;
      }

      if (!sigpadRef.current) {
        return;
      }

      const newSignatureData = sigpadRef.current.toDataURL();

      if (newSignatureData === signatureData) {
        return;
      }

      setSignatureData(newSignatureData);
      saveSignature();
    },
    { delay: 750 }
  );

  const handleClear = () => {
    if (sigpadRef.current) {
      sigpadRef.current.clear();
      setSignatureData(null);
      saveSignature();
    }
  };

  return (
    <Group align="flex-end">
      <SignatureCanvas
        ref={sigpadRef}
        penColor="black"
        canvasProps={{
          width: 500,
          height: 200,
          className: 'sigCanvas',
          style: { border: '1px solid black' },
        }}
        onBegin={() => {
          setWriting(true);
        }}
        onEnd={() => {
          setWriting(false);
          debouncedSaveSignature();
        }}
      />

      <Button variant="light" color="red" onClick={handleClear}>
        {t('questionnaireForm.buttons.clear', 'Clear', { ns: 'medplum-react' })}
      </Button>
      {isSaving && (
        <Text c="gray">
          {t('questionnaireForm.labels.saving', 'Saving...', {
            ns: 'medplum-react',
          })}
        </Text>
      )}
    </Group>
  );
};
